/**
 * EditableBenefitsSection - Why Join/Benefits section with element-level editing
 * 
 * Each element (heading, benefit cards) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import { Eye, Shield, Zap, Users, BookOpen, Pen, Palette, Upload } from 'lucide-react';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_BENEFITS_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Icon mapping
const ICON_MAP = {
  Eye, Shield, Zap, Users, BookOpen, Pen, Palette, Upload
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      void ref.current.offsetWidth;
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  useEffect(() => {
    if (disabled || !ref.current) return;
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) return <div className={className}>{children}</div>;
  return <div ref={ref} className={className}>{children}</div>;
};

const getTextStyleClass = (styles) => {
  const sizeMap = {
    'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base', 'lg': 'text-lg',
    'xl': 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
  };
  const weightMap = {
    'light': 'font-light', 'normal': 'font-normal', 'medium': 'font-medium',
    'semibold': 'font-semibold', 'bold': 'font-bold',
  };
  const alignMap = { 'left': 'text-left', 'center': 'text-center', 'right': 'text-right' };
  
  return `${sizeMap[styles?.size] || 'text-base'} ${weightMap[styles?.weight] || ''} ${alignMap[styles?.alignment] || 'text-center'}`;
};

const EditableBenefitsSection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  const elements = useMemo(() => {
    const map = {};
    LANDING_BENEFITS_ELEMENTS.forEach(el => { map[el.id] = el; });
    return map;
  }, []);

  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  const handleSelectElement = (elementId) => onSelectElement?.(elementId);
  const handleDoubleClick = (elementId) => onStartEditing?.(elementId);
  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Safe string coercion - ensures we never render objects as React children
  const safeString = (val, fallback = '') => {
    if (!val) return fallback;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      if (val.title) return val.title;
      if (val.label) return val.label;
      if (val.text) return val.text;
    }
    return fallback;
  };

  const headingValue = safeString(getValue('benefits.heading'), 'Why build here?');
  const headingStyles = getStyles('benefits.heading');
  const headingMotion = getMotion('benefits.heading');

  const defaultBenefits = [
    { icon: 'Eye', title: 'Discoverability', description: 'Homepage features, trending sections, and personalized recommendations' },
    { icon: 'Shield', title: 'Own your audience', description: 'Direct subscriptions. No algorithm throttling your reach.' },
    { icon: 'Zap', title: 'Creator-first tools', description: 'Episode scheduler, analytics, and reader engagement insights' }
  ];

  const benefits = props.benefits || defaultBenefits;

  return (
    <section className="py-24 bg-black" data-testid="benefits-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <AnimatedElement
          motion={headingMotion}
          animationKey={animationKey}
          disabled={disabled}
          className="text-center mb-16"
        >
          <SelectableElement
            elementId="benefits.heading"
            elementLabel="Section Heading"
            elementType="text"
            isSelected={selectedElementId === 'benefits.heading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <h2 
              className={`text-white ${getTextStyleClass(headingStyles)}`}
              style={{ opacity: headingStyles.opacity ?? 1 }}
              onDoubleClick={() => handleDoubleClick('benefits.heading')}
            >
              {editingElementId === 'benefits.heading' ? (
                <InlineTextEditor
                  value={headingValue}
                  isEditing={true}
                  maxLength={60}
                  onSave={(val) => handleSaveText('benefits.heading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                headingValue
              )}
            </h2>
          </SelectableElement>
        </AnimatedElement>
        
        {/* Benefit Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const elementId = `benefits.item_${index}`;
            const itemMotion = getMotion(elementId);
            const itemStyles = getStyles(elementId);
            const itemValue = getValue(elementId) || benefit;
            
            const normalizedItem = typeof itemValue === 'object' ? itemValue : benefit;
            const IconComponent = ICON_MAP[normalizedItem.icon] || Zap;
            
            return (
              <AnimatedElement 
                key={`benefit-${index}`}
                motion={itemMotion}
                animationKey={animationKey}
                disabled={disabled}
              >
                <SelectableElement
                  elementId={elementId}
                  elementLabel={`Benefit ${index + 1}`}
                  elementType="card"
                  isSelected={selectedElementId === elementId}
                  onSelect={handleSelectElement}
                  disabled={disabled}
                >
                  <div 
                    className="text-center"
                    style={{ opacity: itemStyles.opacity ?? 1 }}
                    onDoubleClick={() => handleDoubleClick(elementId)}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-orange-400" />
                    </div>
                    {editingElementId === elementId ? (
                      <div className="space-y-2">
                        <InlineTextEditor
                          value={normalizedItem.title}
                          isEditing={true}
                          maxLength={40}
                          onSave={(val) => handleSaveText(elementId, { ...normalizedItem, title: val })}
                          onCancel={onStopEditing}
                          className="text-lg font-semibold text-white block"
                        />
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-white mb-2">{normalizedItem.title}</h3>
                        <p className="text-zinc-400 text-sm">{normalizedItem.description}</p>
                      </>
                    )}
                  </div>
                </SelectableElement>
              </AnimatedElement>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EditableBenefitsSection;
